import { createClient } from '@supabase/supabase-js';
import { logger } from './logger';

// Get environment variables with fallbacks for safer initialization
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Log with cautious approach to avoid circular dependency issues
try {
  console.log(`Initializing Supabase client with URL prefix: ${supabaseUrl ? supabaseUrl.substring(0, 10) + '...' : 'missing'}, has anon key: ${!!supabaseAnonKey}`);
} catch (e) {
  console.warn('Error logging Supabase initialization', e);
}

// Validate environment variables before creating client
if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('⚠️ Missing Supabase environment variables. Authentication and database features will not work properly.');
}

// Create client with error handling
let client;

try {
  client = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storageKey: '911cargo.auth.token',
      flowType: 'pkce',
    },
    global: {
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Info': '911cargo@1.0.0'
      }
    },
    // Add request retry for better network resilience
    fetch: (url, options) => {
      const MAX_RETRIES = 3;
      let retries = 0;

      const fetchWithRetry = async (): Promise<Response> => {
        try {
          console.debug('Supabase request:', { 
            url: typeof url === 'string' ? url : url.toString(),
            method: options?.method || 'GET'
          });
          
          // Add timeout to prevent hanging requests
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
          
          // Add signal to options if not already present
          const fetchOptions = {
            ...options,
            signal: options?.signal || controller.signal
          };
          
          const response = await fetch(url, fetchOptions);
          clearTimeout(timeoutId);
          
          console.debug('Supabase response:', {
            status: response.status,
            ok: response.ok,
            statusText: response.statusText
          });
          
          return response;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          const isTimeout = errorMessage.includes('aborted') || errorMessage.includes('timeout');
          const isNetworkError = errorMessage.includes('network') || 
                                 errorMessage.includes('fetch') ||
                                 errorMessage.includes('Failed to fetch');
          
          if ((isNetworkError || isTimeout) && retries < MAX_RETRIES) {
            retries++;
            console.warn(`Retrying Supabase request (${retries}/${MAX_RETRIES}): ${errorMessage}`);
            
            // Exponential backoff
            const delay = Math.pow(2, retries) * 300; 
            await new Promise(resolve => setTimeout(resolve, delay));
            
            return fetchWithRetry();
          }
          
          console.error('Supabase request failed after retries', { 
            error: errorMessage, 
            retries,
            url: typeof url === 'string' ? url : url.toString(),
            method: options?.method || 'GET'
          });
          throw error;
        }
      };

      return fetchWithRetry();
    }
  });
  
  console.log('Supabase client initialized successfully');
  
  // Safe delayed logging through logger now that initialization is complete
  setTimeout(() => {
    try {
      logger.info('Supabase client initialized successfully');
    } catch (e) {
      console.warn('Error logging after Supabase initialization', e);
    }
  }, 0);
} catch (error) {
  console.error('Failed to initialize Supabase client', error);
  
  // Create a fallback client that will provide graceful degradation
  client = {
    auth: {
      signUp: async () => {
        console.error('Auth operations failed - Supabase client not properly initialized');
        return { error: new Error('Unable to connect to authentication service') };
      },
      signInWithPassword: async () => {
        console.error('Auth operations failed - Supabase client not properly initialized');
        return { error: new Error('Unable to connect to authentication service') };
      },
      // Add other auth methods as needed with similar error handling
    },
    from: () => ({
      select: () => ({
        eq: async () => {
          console.error('Database operations failed - Supabase client not properly initialized');
          return { error: new Error('Unable to connect to database service') };
        },
        // Add other query methods as needed
      }),
      insert: async () => {
        console.error('Database operations failed - Supabase client not properly initialized');
        return { error: new Error('Unable to connect to database service') };
      },
      // Add other table methods as needed
    }),
  };
}

// Add global error handler to catch unexpected issues
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.message && 
     (event.reason.message.includes('fetch failed') || 
      event.reason.message.includes('network') ||
      event.reason.message.includes('Failed to fetch'))) {
    console.error('Unhandled Supabase promise rejection (likely network issue)', {
      message: event.reason.message,
      stack: event.reason.stack
    });
  }
});

export const supabase = client;